/* eslint-disable arrow-body-style, object-curly-newline, import/no-extraneous-dependencies,
no-nested-ternary */
import { useState, useEffect, useTransition, useRef, useCallback, useMemo } from 'react'
import { IconButton, InputAdornment, TextField as MUITextField } from '@mui/material'
import { withStyles } from '@mui/styles'
import PlatformEvent from 'lib/util/event'
import { useMemoRef } from '@platform/react/hook'
import Icon from 'ui/Element/Icon'
import { isStr } from 'lib/util'

const styles = () => ({
  root: {
    '& #clear': {
      display: 'none',
      color: 'rgba(0, 0, 0, 0.5)',
    },
    '& #clear:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
    '&:hover #clear': {
      display: 'inline-flex',
    },
    '& input:focus + #clear, & input:active + #clear': {
      display: 'inline-flex',
    },
  },
})

/**
 * ClearAdornment element
 *
 * Returns the clear button adornment, to be appended to the field.
 *
 * @param {Object} classes    the component's style classes
 * @param {function} onClick  the handler to call whenever the button is clicked
 * @param {number} [size=16]  icon dimensions
 * @param {boolean} show      whether the button should be displayed
 * @returns {JSX.Element}     the new ClearAdornment element
 */
export const ClearAdornment = ({ classes, onClick, size = 20, show }) => (
  <InputAdornment
    key={'clear'}
    position={'end'}
    className={classes.clearAdornment}
    sx={{ marginLeft: 0.5 }}
  >
    {show && (
      <IconButton
        id={'clear'}
        onClick={onClick}
        size={'small'}
        tabIndex={-1}
      >
        <Icon
          name={'clear'}
          className={classes.clearIcon}
          color={'black.main'}
          iconPadding={0}
          size={size}
        />
      </IconButton>
    )}
  </InputAdornment>
)

/**
 * Visibility Adornment
 *
 * Returns the visibility button adornment, to be appended to password input fields.
 *
 * @param {string} visibilityIcon     an icon to be used to display the field's contents
 * @param {string} visibilityOffIcon  an icon to be used to hide the field's contents
 * @param {boolean} toggle            `true` to show the `visibilityIcon` or `false` to show the
 *                                    `visibilityOffIcon`
 * @param {number} [size=16]          icon dimensions
 * @param {function} onClick          a function to be called on click
 * @returns {JSX.Element}
 */
const VisibilityAdornment = ({ visibilityIcon, visibilityOffIcon, toggle, size = 20, onClick }) => (
  <InputAdornment
    key={'visibility'}
    position={'end'}
    sx={{ marginLeft: 0.5 }}
  >
    <IconButton
      onClick={onClick}
      size={'small'}
      tabIndex={-1}
    >
      <Icon
        name={toggle ? visibilityOffIcon : visibilityIcon}
        color={'black.main'}
        iconPadding={0}
        size={size}
      />
    </IconButton>
  </InputAdornment>
)

const _getEventValue = e => (e?.target?.value === ' ' ? '' : e?.target?.value)

/**
 * TextField Element
 *
 * Wrapper around {@link MUITextField} intended to adapt the element's functionality to the
 * application's requirements and enable its use and management through its configuration files.
 *
 * @see https://v5.mui.com/material-ui/api/text-field/
 *
 * @param {React.Ref<any>} forwardedRef                 forwarded reference to the root element
 * @param {Object} events                               events for the input field
 * @param {Object} classes                              the component's style classes
 * @param {String} [helperText]                         helper text to be displayed
 * @param {Boolean} [focused]                           whether or not the input should be focused
 * @param {Object} props                                additional props
 * @param {String} [props.value]                        input value
 * @param {String} [props.variant]                      the input style variant
 * @param {Boolean} [props.disabled]                    whether to show the element in disabled
 *                                                      state and prevent the user from using it
 * @param {Boolean} [props.clearable]                   whether to display the clear button
 * @param {String} [props.visibilityIcon]               icon to be shown when the password is
 *                                                      displayed in clear text
 * @param {String} [props.visibilityOffIcon]            icon to be shown when the password is
 *                                                      displayed as dots
 * @param {HTMLInputTypeAttribute|String} [props.type]  type of the input field
 * @returns {*}
 */
const TextField = ({ forwardedRef, events, classes, helperText, focused, ...props }) => {
  const {
    type,
    visibilityIcon,
    visibilityOffIcon,
    disabled,
    clearable,
    group,
    ...restProps
  } = props
  const { onAttachment, onChange, onFeedback, onKeyPress, onBlur } = events || {}

  const [, startTransition] = useTransition()
  const [showPassword, setShowPassword] = useState(false)
  const [value, setValue] = useState(props.value || '')
  const [feedback, setFeedback] = useState(null)
  const inputRef = restProps.inputRef || useRef()

  const password = type === 'password'
  const text = !type || ['text', 'email'].includes(type)

  const clear = useCallback((event) => {
    const newValue = ''
    setValue(newValue)
    setFeedback(newValue)
    inputRef.current?.focus()
    startTransition(() => onChange?.(new PlatformEvent(event, { value: newValue })))
  }, [onChange])

  const handleValueChange = useCallback((newValue) => { setValue(newValue) }, [])

  const handleChange = useCallback((e) => {
    const data = _getEventValue(e)

    handleValueChange(data)

    startTransition(() => {
      const event = new PlatformEvent(e, { value: data })
      onChange?.(event)
      setFeedback(onFeedback?.(event) || '')
    })
  }, [onChange])

  const handleKeyPress = useCallback((e) => {
    const data = _getEventValue(e)

    startTransition(() => {
      onKeyPress?.(new PlatformEvent(e, { value: data }))
    })
  }, [onKeyPress])

  const handleBlur = useCallback((e) => {
    const data = _getEventValue(e)

    startTransition(() => {
      onBlur?.(new PlatformEvent(e, { value: data }))
    })
  }, [onBlur])

  // Handling paste events
  const handleAttachmentPaste = (event) => {
    if (event.clipboardData.files.length) {
      event.clipboardData?.files.length
      && onAttachment?.(new PlatformEvent(event, { files: event.clipboardData.files }))
    }
  }

  useEffect(() => {
    setValue(props.value || '')

    // In case we have a onFeedback handler and the input value changes from the outside
    // we need to execute it again
    if (props.value) {
      const newFeedback = onFeedback?.(new PlatformEvent('change', { value: props.value.toString() }))
      setFeedback(newFeedback || '')
    } else {
      setFeedback('')
    }
  }, [props.value])

  return (
    <MUITextField
      {...restProps}
      ref={forwardedRef}
      inputRef={inputRef}
      className={classes.root}
      classes={{
        adornment: {
          columnGap: 2,
        },
      }}
      fullWidth
      autoFocus={focused}
      onBlur={handleBlur}
      onChange={handleChange}
      onKeyUp={handleKeyPress}
      onPaste={group && handleAttachmentPaste}
      variant={props.variant || 'standard'}
      type={
        type === 'password'
          ? showPassword
            ? 'text'
            : 'password'
          : 'text'
      }
      value={value}
      disabled={disabled}
      helperText={helperText || '\u00A0'}
      FormHelperTextProps={{
        ...restProps.FormHelperTextProps,
        sx: { maxWidth: 'fit-content' },
      }}
      InputProps={{
        ...restProps.InputProps,
        endAdornment: <>
          {feedback && (
            <InputAdornment position={'end'}>
              {feedback}
            </InputAdornment>
          )}
          {(text || password) && clearable && !disabled && (
            <ClearAdornment
              show={!!value?.length}
              classes={classes}
              onClick={clear}
            />
          )}
          {password && (
            <VisibilityAdornment
              visibilityIcon={visibilityIcon}
              visibilityOffIcon={visibilityOffIcon}
              onClick={() => setShowPassword(!showPassword)}
              toggle={showPassword}
            />
          )}
          {restProps.InputProps?.endAdornment}
        </>,
      }}
    />
  )
}

export default useMemoRef(withStyles(styles)(TextField), props => [
  props.value,
  props.label,
  props.placeholder,
  props.helperText,
  props.disabled,
  props.required,
  props.error,
])
